<template>
  <div class="limit">
    <div class="limit__header">
      <div class="limit__header-left">
        <div class="limit__header-left-title">
          <slot name="title" />
        </div>
        <div class="limit__header-left-subtitle">
          <slot name="subtitle" />
        </div>
      </div>
      <div class="limit__header-right">
        <ToolTip :content="showForm ? 'Close and reset limit form.' : 'Add or cancel limit.'">
          <DarwinButton
            size="xs"
            kind="secondary"
            flair="rounded"
            name="add-limit-button"
            aria-label="Add or cancel limit"
            @click="onToggleForm"
          >
            {{ showForm ? 'Cancel' : 'Add limit' }}
          </DarwinButton>
        </ToolTip>
        <ToolTip
          v-if="!isPristine"
          :content="
            selectedLimit === 0
              ? `Remove limit for child team ${selectedOption?.name ?? 'this team'}.`
              : `Set limit for child team ${selectedOption?.name ?? 'this team'}.`
          "
        >
          <DarwinButton
            size="xs"
            kind="primary"
            flair="rounded"
            name="save-button"
            aria-label="Save limit"
            :disabled="disabled"
            @click="onSave"
          >
            Save
          </DarwinButton>
        </ToolTip>
      </div>
    </div>
    <div
      v-if="showForm"
      class="limit__content"
    >
      <div class="limit__content-left">
        <SelectField
          class="limit__content-left-select"
          size="sm"
          :options="options"
          :value="selectedClientId"
          aria-label="Select a team..."
          placeholder="Select a team..."
          :disabled="disabled"
          filterable
          @change="onClientChange"
        >
          <template #option="{ option, selected }">
            <ListItemWithState
              :selected="selected"
              size="md"
              wrap
              :aria-label="option.text"
            >
              <div class="limit__content-left-select-item">
                <div
                  class="limit__content-left-select-item-icon"
                  :style="{ backgroundColor: option.color }"
                />
                {{ option.text }}
              </div>
            </ListItemWithState>
          </template>
        </SelectField>
      </div>
      <div class="limit__content-right">
        <NumberInput
          size="md"
          :min="0"
          :max="maxLimit"
          :model-value="selectedLimit"
          :disabled="disabled"
          @update:model-value="selectedLimit = $event"
        />
        <ToolTip content="See more options for this child team.">
          <PopOverMenu
            :open="removeMenuOpen"
            placement="right-start"
            @click:outside="removeMenuOpen = false"
          >
            <template #target>
              <IconButton
                size="sm"
                flair="soft"
                kind="transparent"
                name="menu-button"
                aria-label="See more options"
                :disabled="disabled"
                @click="removeMenuOpen = !removeMenuOpen"
              >
                <Icon name="mono-dots-vertical" />
              </IconButton>
            </template>
            <template #menu>
              <ListItemWithState
                size="md"
                aria-label="Remove limit"
                negative
                @click="onRemoveLimit"
              >
                <template #prepend>
                  <Icon name="mono-close" />
                </template>
                <span class="limit__content-right-menu-item-text"> Remove limit </span>
              </ListItemWithState>
            </template>
          </PopOverMenu>
        </ToolTip>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import NumberInput from '@/uiKit/NumberInput/NumberInput.vue'
import SelectField from '@/uiKit/SelectField/SelectField.vue'
import IconButton from '@/uiKit/Button/IconButton.vue'
import Icon from '@/uiKit/Icon/Icon.vue'
import ToolTip from '@/uiKit/ToolTip/ToolTip.vue'
import ListItemWithState from '@/uiKit/List/ListItemWithState.vue'
import type { ClientTeamLimitMap } from './types'
import type { OptionType } from '@/uiKit/types'
import PopOverMenu from '@/uiKit/PopOverMenu.vue'

const props = defineProps<{
  childTeamsLimitMap: ClientTeamLimitMap
  type: 'creditLimit' | 'storageLimit'
  maxLimit?: number
  disabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'save', payload: { clientSlug: string; partnerId: number; value: number }): void
}>()

const selectedClientId = ref<string>('')
const showForm = ref(false)
const selectedLimit = ref(0)
const removeMenuOpen = ref(false)

const selectedOption = computed(() => props.childTeamsLimitMap[selectedClientId.value])

const childTeamsLimitList = computed(() => Object.values(props.childTeamsLimitMap))

const currentValue = computed(
  () => props.childTeamsLimitMap[selectedClientId.value]?.[props.type] ?? 0,
)

const isPristine = computed(() => selectedLimit.value === currentValue.value)

const options = computed<OptionType[]>(() =>
  childTeamsLimitList.value?.map((o) => ({
    id: `${o?.id}`,
    color: o?.color,
    text: o?.name,
    value: o?.id,
  })),
)

const onToggleForm = (): void => {
  showForm.value = !showForm.value
  selectedLimit.value = currentValue.value ?? 0
  removeMenuOpen.value = false
}

const onClientChange = ({ id }: OptionType): void => {
  selectedClientId.value = id
  selectedLimit.value = currentValue.value ?? 0
}

const onSave = (): void => {
  if (!selectedOption.value) {
    return
  }

  emit('save', {
    clientSlug: selectedClientId.value,
    partnerId: selectedOption.value.partnerId,
    value: selectedLimit.value,
  })
}

const onRemoveLimit = (): void => {
  removeMenuOpen.value = false

  if (!selectedOption.value) {
    return
  }

  emit('save', {
    clientSlug: selectedClientId.value,
    partnerId: selectedOption.value.partnerId,
    value: 0,
  })
}

watch(
  () => childTeamsLimitList.value.length,
  (value) => {
    if (!value) {
      return
    }

    selectedClientId.value = childTeamsLimitList.value?.[0]?.id ?? ''
    selectedLimit.value = currentValue.value ?? 0
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';

.limit {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-self: center;
  gap: var(--size-4);
  width: 100%;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;

    &-left {
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      align-items: flex-start;

      &-title {
        @include fontRegularBody200;
        color: var(--color-text-default);
        font-weight: 600;
      }

      &-subtitle {
        @include fontRegularBody200;
        color: var(--color-text-subtlest);
        font-weight: 500;
      }
    }

    &-right {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      gap: var(--size-1);
    }
  }

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;

    &-left {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: var(--size-1);

      &-select-item {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: var(--size-2);

        &-icon {
          width: var(--size-2);
          height: var(--size-2);
          border-radius: 50%;
        }
      }
    }

    &-right {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: var(--size-1);

      &-menu-item-text {
        padding-right: var(--size-2);
      }
    }
  }
}
</style>
